/**
 * Log Transports
 * Console and remote logging transports
 * 
 * @module lib/logger/transports
 */

import { LogEntry, LogLevel, LogTransport, LOG_LEVEL_PRIORITY } from './types';

// =============================================================================
// Console Transport
// =============================================================================

const LEVEL_COLORS: Record<LogLevel, string> = {
    debug: '#9CA3AF', // gray
    info: '#3B82F6',  // blue
    warn: '#F59E0B',  // yellow
    error: '#EF4444', // red
    fatal: '#DC2626', // dark red
};

const LEVEL_ICONS: Record<LogLevel, string> = {
    debug: '🔍',
    info: 'ℹ️',
    warn: '⚠️',
    error: '❌',
    fatal: '💀',
};

/**
 * Console transport for development logging
 * Pretty-prints logs with colors and formatting
 */
export class ConsoleTransport implements LogTransport {
    name = 'console';
    minLevel: LogLevel;
    private useColors: boolean;

    constructor(options: { minLevel?: LogLevel; useColors?: boolean } = {}) {
        this.minLevel = options.minLevel ?? 'debug';
        this.useColors = options.useColors ?? true;
    }

    log(entry: LogEntry): void {
        if (LOG_LEVEL_PRIORITY[entry.level] < LOG_LEVEL_PRIORITY[this.minLevel]) {
            return;
        }

        const icon = LEVEL_ICONS[entry.level];
        const timestamp = new Date(entry.timestamp).toLocaleTimeString();
        const component = entry.context.component ? `[${entry.context.component}]` : '';

        // Build the message parts
        const prefix = `${icon} ${timestamp} ${component}`.trim();

        // Choose console method
        const consoleMethod = this.getConsoleMethod(entry.level);

        if (this.useColors && typeof window !== 'undefined') {
            // Browser with colors
            // eslint-disable-next-line no-console
            console[consoleMethod](
                `%c${prefix}%c ${entry.message}`,
                `color: ${LEVEL_COLORS[entry.level]}; font-weight: bold;`,
                'color: inherit;',
                entry.data ? entry.data : '',
                entry.error ? entry.error : ''
            );
        } else {
            // Node.js or no colors
            // eslint-disable-next-line no-console
            console[consoleMethod](
                `${prefix} ${entry.message}`,
                entry.data ?? '',
                entry.error ?? ''
            );
        }

        // Log stack trace for errors in development
        if (entry.error?.stack && entry.level === 'error' || entry.level === 'fatal') {
            // eslint-disable-next-line no-console
            console.groupCollapsed('Stack Trace');
            // eslint-disable-next-line no-console
            console.log(entry.error.stack);
            // eslint-disable-next-line no-console
            console.groupEnd();
        }
    }

    private getConsoleMethod(level: LogLevel): 'log' | 'info' | 'warn' | 'error' {
        switch (level) {
            case 'debug': return 'log';
            case 'info': return 'info';
            case 'warn': return 'warn';
            case 'error':
            case 'fatal': return 'error';
            default: return 'log';
        }
    }
}

// =============================================================================
// Remote Transport
// =============================================================================

interface RemoteTransportOptions {
    /** API endpoint to send logs to */
    endpoint?: string;
    /** Minimum log level to send remotely */
    minLevel?: LogLevel;
    /** Batch size before sending */
    batchSize?: number;
    /** Flush interval in milliseconds */
    flushInterval?: number;
    /** Custom headers */
    headers?: Record<string, string>;
    /** Transform function before sending */
    transform?: (entry: LogEntry) => Record<string, unknown>;
}

/**
 * Remote transport for sending logs to external service
 * Batches logs and sends them periodically
 */
export class RemoteTransport implements LogTransport {
    name = 'remote';
    minLevel: LogLevel;

    private endpoint: string;
    private batchSize: number;
    private flushInterval: number;
    private headers: Record<string, string>;
    private transform?: (entry: LogEntry) => Record<string, unknown>;

    private queue: LogEntry[] = [];
    private flushTimer: ReturnType<typeof setInterval> | null = null;
    private isFlushing = false;

    constructor(options: RemoteTransportOptions = {}) {
        this.endpoint = options.endpoint ?? '/api/logs';
        this.minLevel = options.minLevel ?? 'error';
        this.batchSize = options.batchSize ?? 10;
        this.flushInterval = options.flushInterval ?? 30000; // 30 seconds
        this.headers = options.headers ?? {};
        this.transform = options.transform;

        // Start flush timer
        this.startFlushTimer();

        // Flush on page unload
        if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', () => {
                this.flush();
            });
        }
    }

    log(entry: LogEntry): void {
        if (LOG_LEVEL_PRIORITY[entry.level] < LOG_LEVEL_PRIORITY[this.minLevel]) {
            return;
        }

        this.queue.push(entry);

        // Flush if batch size reached
        if (this.queue.length >= this.batchSize) {
            this.flush();
        }
    }

    async flush(): Promise<void> {
        if (this.isFlushing || this.queue.length === 0) {
            return;
        }

        this.isFlushing = true;
        const batch = [...this.queue];
        this.queue = [];

        try {
            const payload = batch.map(entry =>
                this.transform ? this.transform(entry) : entry
            );

            await fetch(this.endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.headers,
                },
                body: JSON.stringify({ logs: payload }),
                // Use keepalive for page unload scenarios
                keepalive: true,
            });
        } catch (error) {
            // Put logs back in queue on failure (limited retry)
            if (batch.length < this.batchSize * 2) {
                this.queue = [...batch, ...this.queue];
            }
            console.error('[RemoteTransport] Failed to send logs:', error);
        } finally {
            this.isFlushing = false;
        }
    }

    private startFlushTimer(): void {
        if (this.flushTimer) {
            clearInterval(this.flushTimer);
        }
        this.flushTimer = setInterval(() => {
            this.flush();
        }, this.flushInterval);
    }

    destroy(): void {
        if (this.flushTimer) {
            clearInterval(this.flushTimer);
            this.flushTimer = null;
        }
    }
}

import { captureException as sentryCaptureException, captureMessage as sentryCaptureMessage } from '../monitoring/sentry';

/**
 * Sentry transport - forwards logs to Sentry
 */
export class SentryTransport implements LogTransport {
    name = 'sentry';
    minLevel: LogLevel;

    constructor(options: { minLevel?: LogLevel } = {}) {
        this.minLevel = options.minLevel ?? 'error';
    }

    log(entry: LogEntry): void {
        if (LOG_LEVEL_PRIORITY[entry.level] < LOG_LEVEL_PRIORITY[this.minLevel]) {
            return;
        }

        if (entry.error) {
            // Unwrap our mapped error for Sentry if it exists
            const rawError = entry.error instanceof Error ? entry.error : new Error(entry.error.message);
            sentryCaptureException(rawError, {
                extra: entry.data,
                tags: {
                    component: entry.context.component,
                    level: entry.level
                },
            });
        } else {
            const levelMap: Record<string, string> = {
                debug: 'debug',
                info: 'info',
                warn: 'warning',
                error: 'error',
                fatal: 'fatal'
            };
            sentryCaptureMessage(entry.message, {
                level: (levelMap[entry.level as keyof typeof levelMap] || 'info') as any,
                extra: entry.data,
                tags: { component: entry.context.component },
            });
        }
    }

    async flush(): Promise<void> {
        // Sentry SDK handles its own flushing
    }
}
